import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../api';
import { useSiteSettings } from '../SiteSettingsContext';
import { toast } from 'sonner';
import { Stethoscope, Mail, Lock, Eye, EyeOff, Shield, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

const LoginPage = () => {
  const { login, loginWithGoogle, user, setUser } = useAuth();
  const { logoUrl, navIconUrl, orgName } = useSiteSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [pendingToken, setPendingToken] = useState('');
  const [twoFAMethod, setTwoFAMethod] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const codeInputRef = useRef(null);

  const from = location.state?.from?.pathname || new URLSearchParams(location.search).get('redirect') || '/dashboard';

  // Show a helpful toast if the user was just booted out due to session expiry
  // (Safari ITP clearing localStorage, server-side session expiry, corporate
  // proxies stripping auth headers, etc.)
  useEffect(() => {
    try {
      if (sessionStorage.getItem('auth_expired_flag')) {
        sessionStorage.removeItem('auth_expired_flag');
        toast.info('Your session expired. Please sign in again to continue.');
      }
    } catch {}
  }, []);

  // Redirect if already logged in — check for guardian status
  useEffect(() => {
    if (user) {
      api.get('/guardian/my-dependents-for-picker').then(res => {
        if (res.data && res.data.length > 0) {
          navigate('/account-picker', { replace: true });
        } else {
          navigate(from, { replace: true });
        }
      }).catch(() => {
        navigate(from, { replace: true });
      });
    }
  }, [user, navigate, from]);

  // Focus code input when 2FA is required
  useEffect(() => {
    if (requires2FA && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [requires2FA]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await api.post('/auth/login', { email, password });
      
      if (response.data.requires_2fa) {
        // 2FA is required
        setRequires2FA(true);
        setPendingToken(response.data.pending_token);
        setTwoFAMethod(response.data.method);
        setEmailHint(response.data.email_hint);
        
        if (response.data.method === 'email') {
          toast.success('Verification code sent to your email');
        }
      } else {
        // No 2FA, login successful
        setUser(response.data);
        toast.success('Welcome back!');
        navigate(from, { replace: true });
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!verificationCode.trim()) {
      toast.error('Please enter the verification code');
      return;
    }
    
    setVerifying(true);
    try {
      const response = await api.post('/auth/verify-2fa', {
        pending_token: pendingToken,
        code: verificationCode.trim()
      });
      
      setUser(response.data);
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid verification code';
      toast.error(message);
      setVerificationCode('');
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    try {
      await api.post('/auth/resend-2fa-code', { pending_token: pendingToken });
      toast.success('New verification code sent');
      setVerificationCode('');
    } catch (error) {
      toast.error('Failed to resend code. Please try logging in again.');
    } finally {
      setResending(false);
    }
  };

  const handleBack = () => {
    setRequires2FA(false);
    setPendingToken('');
    setVerificationCode('');
    setPassword('');
  };

  // 2FA Verification Screen
  if (requires2FA) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-amber-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-12 h-12 rounded-xl overflow-hidden bg-slate-900 flex items-center justify-center">
                {navIconUrl ? (
                  <img src={navIconUrl} alt={orgName} className="w-full h-full object-contain" />
                ) : (
                  <Stethoscope className="w-7 h-7 text-white" />
                )}
              </div>
              <span className="font-heading font-bold text-2xl text-slate-800">{orgName}</span>
            </Link>
          </div>

          <Card className="border-0 shadow-xl rounded-2xl overflow-hidden">
            <CardHeader className="text-center pb-2 pt-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="text-2xl font-heading font-bold text-slate-800">
                Two-Factor Authentication
              </CardTitle>
              <CardDescription className="text-slate-500">
                {twoFAMethod === 'email' 
                  ? `Enter the code sent to ${emailHint}`
                  : 'Enter the code from your authenticator app'
                }
              </CardDescription>
            </CardHeader>
            
            <CardContent className="p-8 pt-4">
              <form noValidate onSubmit={handleVerify2FA} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="code" className="text-slate-700">Verification Code</Label>
                  <Input
                    ref={codeInputRef}
                    id="code"
                    data-testid="2fa-code-input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    className="h-14 text-center text-2xl tracking-[0.5em] font-mono rounded-xl border-slate-200 focus:ring-primary/20 focus:border-primary"
                    required
                  />
                </div>

                <Button
                  data-testid="verify-2fa-btn"
                  type="submit"
                  className="w-full h-12 rounded-xl bg-rojo hover:bg-rojo-dark font-semibold"
                  disabled={verifying || verificationCode.length < 6}
                >
                  {verifying ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Verify'
                  )}
                </Button>

                {twoFAMethod === 'email' && (
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={resending}
                      className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                    >
                      {resending ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      Resend code
                    </button>
                  </div>
                )}

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="text-sm text-slate-500 hover:text-slate-700"
                  >
                    ← Back to login
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Normal Login Screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-amber-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-12 h-12 rounded-xl overflow-hidden bg-slate-900 flex items-center justify-center">
              {navIconUrl ? (
                <img src={navIconUrl} alt={orgName} className="w-full h-full object-contain" />
              ) : (
                <Stethoscope className="w-7 h-7 text-white" />
              )}
            </div>
            <span className="font-heading font-bold text-2xl text-slate-800">{orgName}</span>
          </Link>
        </div>

        <Card className="border-0 shadow-xl rounded-2xl overflow-hidden">
          <CardHeader className="text-center pb-2 pt-8">
            <CardTitle className="text-2xl font-heading font-bold text-slate-800">
              Welcome Back
            </CardTitle>
            <CardDescription className="text-slate-500">
              Sign in to access your account
            </CardDescription>
          </CardHeader>
          
          <CardContent className="p-8 pt-4">
            {/* Google Sign In */}
            <Button
              data-testid="google-signin-btn"
              type="button"
              variant="outline"
              className="w-full h-12 rounded-xl border-slate-200 hover:bg-slate-50 mb-6"
              onClick={loginWithGoogle}
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-4 text-slate-400">Or continue with email</span>
              </div>
            </div>

            <form noValidate onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 rounded-xl border-slate-200 focus:ring-primary/20 focus:border-primary"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-slate-700">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    data-testid="password-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-12 rounded-xl border-slate-200 focus:ring-primary/20 focus:border-primary"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                data-testid="login-submit-btn"
                type="submit"
                className="w-full h-12 rounded-xl bg-rojo hover:bg-rojo-dark font-semibold"
                disabled={loading}
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-slate-500">
              Don't have an account?{' '}
              <Link to="/signup" className="text-primary font-medium hover:underline" data-testid="signup-link">
                Request Access
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-6">
          By signing in, you agree to our terms of service and privacy policy.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
